import { useEffect, useState } from 'react'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { RecordType } from '@factorialco/f0-react/dist/experimental'
import { ImportHistoryDocument } from 'generated/resources/graphql'
import { ImportHistoryQuery } from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  getImportTypename,
  mapStatus,
  processedRowsLabel,
} from 'modules/universal_importer/common/helpers'

// Import type keys
const IMPORT_TYPE_KEYS = [
  'employee_importer',
  'candidates_importer',
  'employee_edition',
  'employee_bulk_termination_importer',
  'fd_contract_importer',
  'payroll_summary_importer',
  'competencies_competency_importer',
  'competencies_competency_assignment_importer',
  'compensations_employee_compensation_importer',
  'it_management_it_assets_importer',
  'ats_job_posting_importer',
  'benefits_healthcare_beneficiaries_importer',
  'bulk_email_change',
] as const

// Dynamic helper function to get import type options
function getImportTypeOptions() {
  const options = {
    employee_importer: i18n.t(
      'universal_importer.import_history.table.filters.type.options.employee_importer'
    ),
    candidates_importer: i18n.t(
      'universal_importer.import_history.table.filters.type.options.candidates_importer'
    ),
    employee_edition: i18n.t(
      'universal_importer.import_history.table.filters.type.options.employee_edition'
    ),
    employee_bulk_termination_importer: i18n.t(
      'universal_importer.import_history.table.filters.type.options.employee_bulk_termination_importer'
    ),
    fd_contract_importer: i18n.t(
      'universal_importer.import_history.table.filters.type.options.fd_contract_importer'
    ),
    payroll_summary_importer: i18n.t(
      'universal_importer.import_history.table.filters.type.options.payroll_summary_importer'
    ),
    competencies_competency_importer: i18n.t(
      'universal_importer.import_history.table.filters.type.options.competencies_competency_importer'
    ),
    competencies_competency_assignment_importer: i18n.t(
      'universal_importer.import_history.table.filters.type.options.competencies_competency_assignment_importer'
    ),
    compensations_employee_compensation_importer: i18n.t(
      'universal_importer.import_history.table.filters.type.options.compensations_employee_compensation_importer'
    ),
    cost_center_assignments_importer: i18n.t(
      'universal_importer.import_history.table.filters.type.options.cost_center_assignments_importer'
    ),
    it_management_it_assets_importer: i18n.t(
      'universal_importer.import_history.table.filters.type.options.it_management_it_assets_importer'
    ),
    ats_job_posting_importer: i18n.t(
      'universal_importer.import_history.table.filters.type.options.ats_job_posting_importer'
    ),
    benefits_healthcare_beneficiaries_importer: i18n.t(
      'universal_importer.import_history.table.filters.type.options.benefits_healthcare_beneficiaries_importer'
    ),
    job_catalog_tree_importer: i18n.t(
      'universal_importer.import_history.table.filters.type.options.job_catalog_tree_importer'
    ),
    contract_version_edition: i18n.t(
      'universal_importer.import_history.table.filters.type.options.contract_version_edition'
    ),
    contract_version_importer: i18n.t(
      'universal_importer.import_history.table.filters.type.options.contract_version_importer'
    ),
    teams_importer: i18n.t(
      'universal_importer.import_history.table.filters.type.options.teams_importer'
    ),
    bulk_email_change: i18n.t(
      'universal_importer.import_history.table.filters.type.options.bulk_email_change'
    ),
  }

  return IMPORT_TYPE_KEYS.map((key) => ({
    label: options[key],
    value: key,
  }))
}

interface ImportHistoryItem extends RecordType {
  importType: string
  legalEntity?: { __typename?: 'CompaniesLegalEntity'; id: number; legalName: string } | null
  status: string
  processed: number
  total: number
  access?: {
    fullName: string
    avatar?: string | null
  } | null
  updatedAt: string
  errored: number
  failedMessage?: string | null
}

function ImportHistoryDataCollection() {
  const { navigation } = useNavigation()
  const [failedMessage, setFailedMessage] = useState<string | null>(null)

  useEffect(() => {
    if (failedMessage) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('universal_importer.import_history.table.failed_message', {
          failedMessage,
        }),
      })

      setFailedMessage(null)
    }
  }, [failedMessage])

  const source = useGraphqlDataCollectionSource({
    document: ImportHistoryDocument,
    itemOnClick: (item) => {
      return () => {
        if (item.errored) {
          const url =
            navigation.settings.universalImporter.universalImporterProcess
              .fullPath({
                importProcessId: Number(item.id),
              })
              .replace(/\*+$/, '') +
            `?failed_message=${encodeURIComponent(item.failedMessage ?? '')}`
          window.location.href = url
        } else {
          setFailedMessage(item.failedMessage ?? null)
        }
      }
    },
    mapData: (data: ImportHistoryQuery): ImportHistoryItem[] => {
      return data.universalImporter.importProcessesConnection.edges.map(
        (edge): ImportHistoryItem => edge.node
      )
    },
    mapPagination: (data) => {
      return {
        total: Number(data.universalImporter.importProcessesConnection.totalCount),
      }
    },
    search: { enabled: true },
    mapVariables: ({ sortings, filters, search }) => {
      return {
        sort: sortings?.[0],
        filters: filters?.importType?.length
          ? {
              importType: { in: filters.importType },
            }
          : undefined,
        first: 20,
        search,
      }
    },
    filters: {
      importType: {
        label: i18n.t('universal_importer.import_history.table.filters.type.title'),
        type: 'in',
        default: true,
        options: {
          options: getImportTypeOptions(),
        },
      },
    },
    sortings: {
      importType: {
        label: i18n.t('universal_importer.import_history.table.headers.type'),
      },
      status: {
        label: i18n.t('universal_importer.import_history.table.headers.status'),
      },
      processed: {
        label: i18n.t('universal_importer.import_history.table.headers.imported_rows'),
      },
      updatedAt: {
        label: i18n.t('universal_importer.import_history.table.headers.import_date'),
      },
      errored: {
        label: i18n.t('universal_importer.import_history.table.headers.errors'),
      },
    },
  })

  return (
    <>
      <OneDataCollection
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('universal_importer.import_history.table.headers.type'),
                  sorting: 'importType',
                  render: (item: ImportHistoryItem) => {
                    const importType = getImportTypename(
                      String(item.importType),
                      String(item?.legalEntity?.legalName)
                    )
                    return {
                      type: 'text',
                      value: importType,
                    }
                  },
                },
                {
                  label: i18n.t('universal_importer.import_history.table.headers.status'),
                  sorting: 'status',
                  render: (item) => ({
                    type: 'status',
                    value: mapStatus(item.status),
                  }),
                },
                {
                  label: i18n.t('universal_importer.import_history.table.headers.imported_rows'),
                  sorting: 'processed',
                  render: (item) => {
                    return {
                      type: 'text',
                      value: processedRowsLabel(item.processed, item.total),
                    }
                  },
                },
                {
                  label: i18n.t('universal_importer.import_history.table.headers.imported_by'),
                  render: (item) => {
                    const [firstName, lastName] = item.access?.fullName?.split(' ') ?? []
                    return {
                      type: 'person',
                      value: {
                        firstName: firstName ?? '',
                        lastName: lastName ?? '',
                        src: item.access?.avatar ? buildUrl(item.access.avatar) : '',
                      },
                    }
                  },
                },
                {
                  label: i18n.t('universal_importer.import_history.table.headers.import_date'),
                  sorting: 'updatedAt',
                  render: ({ updatedAt }) => ({
                    value: i18nDateToString(
                      stringToDate(updatedAt, 'backend_datetime'),
                      'ui_short_with_time'
                    ),
                    type: 'text',
                  }),
                },
                {
                  label: i18n.t('universal_importer.import_history.table.headers.errors'),
                  sorting: 'errored',
                  render: ({ errored }) => {
                    if (!errored || errored === 0) {
                      return {
                        type: 'text',
                        value: '',
                      }
                    }

                    return {
                      type: 'status',
                      value: {
                        label: String(errored),
                        status: 'critical',
                      },
                    }
                  },
                },
              ],
            },
          },
        ]}
      />
    </>
  )
}

export default ImportHistoryDataCollection
